/**
 * React Query hooks for dashboard data management
 * Provides automatic caching, background updates, and optimistic UI
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dashboardService, Dashboard } from '../services/dashboardService';

/**
 * Hook to fetch all dashboards for the current user
 * Cached for 5 minutes, refetches in background
 */
export const useDashboards = () => {
  return useQuery({
    queryKey: ['dashboards', 'list'],
    queryFn: () => dashboardService.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnMount: 'always', // Always check for updates on mount
  });
};

/**
 * Hook to fetch a single dashboard by ID
 * Cached per dashboard ID
 */
export const useDashboard = (dashboardId: string | null) => {
  return useQuery({
    queryKey: ['dashboards', 'detail', dashboardId],
    queryFn: async () => {
      if (!dashboardId) return null;
      return dashboardService.getById(dashboardId);
    },
    enabled: !!dashboardId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false, // Don't refetch on every mount since data rarely changes
  });
};

/**
 * Hook to create a new dashboard
 * Automatically invalidates dashboard list on success
 */
export const useCreateDashboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dashboard: Omit<Dashboard, 'id' | 'createdAt'>) =>
      dashboardService.create(dashboard),
    onSuccess: (newDashboardId) => {
      // Invalidate and refetch dashboard list
      queryClient.invalidateQueries({ queryKey: ['dashboards', 'list'] });

      // If we got a dashboard ID, prefetch its details
      if (newDashboardId) {
        queryClient.prefetchQuery({
          queryKey: ['dashboards', 'detail', newDashboardId],
          queryFn: () => dashboardService.getById(newDashboardId),
        });
      }
    },
  });
};

/**
 * Hook to update an existing dashboard
 * Automatically invalidates relevant caches on success
 */
export const useUpdateDashboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Omit<Dashboard, 'id' | 'createdAt'>>;
    }) => dashboardService.update(id, updates),
    onSuccess: (_success, variables) => {
      // Invalidate the specific dashboard and the list
      queryClient.invalidateQueries({ queryKey: ['dashboards', 'detail', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['dashboards', 'list'] });
    },
  });
};

/**
 * Hook to delete a dashboard
 * Automatically removes from cache on success
 */
export const useDeleteDashboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dashboardId: string) => dashboardService.delete(dashboardId),
    onSuccess: (_success, dashboardId) => {
      // Remove the specific dashboard from cache
      queryClient.removeQueries({ queryKey: ['dashboards', 'detail', dashboardId] });

      // Invalidate and refetch dashboard list
      queryClient.invalidateQueries({ queryKey: ['dashboards', 'list'] });
    },
  });
};

/**
 * Hook for dashboard cache management utilities
 */
export const useDashboardCache = () => {
  const queryClient = useQueryClient();

  const invalidateAllDashboards = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboards'] });
  };

  const prefetchDashboard = (dashboardId: string) => {
    queryClient.prefetchQuery({
      queryKey: ['dashboards', 'detail', dashboardId],
      queryFn: () => dashboardService.getById(dashboardId),
    });
  };

  return {
    invalidateAllDashboards,
    prefetchDashboard,
  };
};
